/**
 * Forgot Password Page - Recuperação de Senha
 * Supports: Email link recovery OR temporary code from admin
 */
import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail, Key, Clock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tokenFromUrl = searchParams.get('token');
  
  const [mode, setMode] = useState(tokenFromUrl ? 'reset' : 'choose'); // choose, email, code, reset
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Send email recovery link
  const handleSendEmailLink = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Digite seu email');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      toast.success('Se o email existir, você receberá um link de recuperação');
      setMode('email-sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setLoading(false);
    }
  };

  // Reset password with temporary code
  const handleResetWithCode = async (e) => {
    e.preventDefault();
    
    if (!email || !code) {
      toast.error('Preencha o email e o código');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-with-code`, {
        email,
        code,
        new_password: newPassword
      });
      toast.success('Senha alterada com sucesso!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  // Reset password with email link token
  const handleResetWithToken = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token: tokenFromUrl,
        new_password: newPassword
      });
      toast.success('Senha alterada com sucesso!');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Link inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <Button 
            variant="ghost" 
            className="absolute left-4 top-4"
            onClick={() => mode === 'choose' ? navigate('/login') : setMode('choose')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          <div className="mx-auto w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-4 mt-8">
            <Key className="w-8 h-8 text-cyan-600" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Recuperar Senha
          </CardTitle>
          <CardDescription className="text-slate-600">
            {mode === 'choose' && 'Escolha como deseja recuperar sua senha'}
            {mode === 'email' && 'Digite seu email para receber o link de recuperação'}
            {mode === 'email-sent' && 'Email enviado!'}
            {mode === 'code' && 'Digite o código fornecido pelo administrador'}
            {mode === 'reset' && 'Crie uma nova senha para sua conta'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Choose Mode */}
          {mode === 'choose' && (
            <div className="space-y-4">
              <Button
                variant="outline"
                className="w-full h-auto py-4 flex items-start gap-4"
                onClick={() => setMode('email')}
              >
                <Mail className="w-6 h-6 text-cyan-600 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Receber link por email</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Enviaremos um link de recuperação para seu email cadastrado
                  </p>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="w-full h-auto py-4 flex items-start gap-4"
                onClick={() => setMode('code')}
              >
                <Clock className="w-6 h-6 text-blue-600 mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">Tenho um código</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Use o código de 6 dígitos fornecido pelo administrador
                  </p>
                </div>
              </Button>
              
              <div className="text-center pt-4">
                <Link 
                  to="/login" 
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  Lembrou a senha? Fazer login
                </Link>
              </div>
            </div>
          )}

          {/* Email Mode */}
          {mode === 'email' && (
            <form onSubmit={handleSendEmailLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email cadastrado</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Enviar Link de Recuperação
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Email Sent Confirmation */}
          {mode === 'email-sent' && (
            <div className="text-center space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-green-700">
                  Se o email <strong>{email}</strong> estiver cadastrado, você receberá um link de recuperação em alguns minutos.
                </p>
              </div>
              <p className="text-sm text-slate-500">
                Verifique também sua caixa de spam.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                Voltar ao Login
              </Button>
            </div>
          )}

          {/* Code Mode */}
          {mode === 'code' && (
            <form onSubmit={handleResetWithCode} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-code">Email cadastrado</Label>
                <Input
                  id="email-code"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="temp-code">Código de 6 dígitos</Label>
                <Input
                  id="temp-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl font-mono tracking-widest"
                  maxLength={6}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-password-code">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="new-password-code"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password-code">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="confirm-password-code"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme sua nova senha"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Alterando...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Alterar Senha
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Reset with Token Mode (from email link) */}
          {mode === 'reset' && (
            <form onSubmit={handleResetWithToken} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-700">
                  Crie uma nova senha para sua conta.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-password-token">Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="new-password-token"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm-password-token">Confirmar Nova Senha</Label>
                <div className="relative">
                  <Input
                    id="confirm-password-token"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirme sua nova senha"
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Alterando...
                  </>
                ) : (
                  <>
                    <Key className="w-4 h-4 mr-2" />
                    Criar Nova Senha
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
